import { Controller } from '@hotwired/stimulus';

/**
 * Experience Create Controller
 *
 * Handles the experience creation form with:
 * - Multiple image uploads (up to 6) with S3 direct upload
 * - Grid-based preview layout
 * - Star rating selection
 * - Form validation
 * - Populates hidden input with S3 keys for HTMX form submission
 */
export default class extends Controller {
  static targets = [
    'fileInput',
    'mediaSlot',
    'addButton',
    'preview',
    'spinner',
    'deleteButton',
    'description',
    'ratingContainer',
    'star',
    'ratingInput',
    'submitButton',
    's3KeysInput',
  ];

  static values = {
    imageUploadUrl: String,
  };

  connect() {
    this.uploadedMedia = []; // Array of { s3_key, file, index }
    this.maxFiles = 6;
    this.selectedRating = 0;
    this.updateAddButtonVisibility();
  }

  // Open file picker
  openFilePicker(event) {
    event.preventDefault();
    this.fileInputTarget.click();
  }

  // Handle file selection
  async handleFileSelect(event) {
    const files = Array.from(event.target.files);

    // Calculate how many slots are available
    const availableSlots = this.maxFiles - this.uploadedMedia.length;
    const filesToUpload = files.slice(0, availableSlots);

    if (filesToUpload.length === 0) {
      return;
    }

    // Upload each file
    for (const file of filesToUpload) {
      await this.uploadFile(file);
    }

    // Clear the file input
    this.fileInputTarget.value = '';
  }

  // Upload single file to S3
  async uploadFile(file) {
    // Find next available slot index
    const slotIndex = this.getNextAvailableSlotIndex();
    if (slotIndex === null) {
      console.error('No available slots');
      return;
    }

    try {
      // Show spinner
      this.showSpinner(slotIndex);

      // Step 1: Get presigned URL
      const presignedData = await this.requestPresignedUrl(file);

      // Step 2: Upload to S3
      await this.uploadToS3(file, presignedData);

      // Step 3: Show preview
      this.showPreview(slotIndex, file);

      // Step 4: Store upload data
      this.uploadedMedia.push({
        s3_key: presignedData.s3_key,
        file: file.name,
        index: slotIndex,
      });

      // Hide spinner, show delete button
      this.hideSpinner(slotIndex);
      this.showDeleteButton(slotIndex);

      // Update UI
      this.updateAddButtonVisibility();
      this.validateForm();
    } catch (error) {
      console.error('Upload failed:', error);
      this.hideSpinner(slotIndex);
      alert(`Failed to upload ${file.name}: ${error.message}`);
    }
  }

  // Request presigned URL from Django
  async requestPresignedUrl(file) {
    const csrfToken = this.getCsrfToken();

    const response = await fetch(this.imageUploadUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type,
        upload_type: 'experience_photo',
        max_file_size_mb: 20,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to get upload URL' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Upload file to S3 using presigned URL
  async uploadToS3(file, presignedData) {
    const formData = new FormData();

    // Add presigned fields first (order matters for S3)
    Object.entries(presignedData.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Add file last
    formData.append('file', file);

    const response = await fetch(presignedData.upload_url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`);
    }
  }

  // Get next available slot index
  getNextAvailableSlotIndex() {
    const usedIndices = this.uploadedMedia.map((m) => m.index);
    for (let i = 0; i < this.maxFiles; i++) {
      if (!usedIndices.includes(i)) {
        return i;
      }
    }
    return null;
  }

  // Show image preview
  showPreview(index, file) {
    const preview = this.previewTargets.find((p) => parseInt(p.dataset.index) === index);
    const addButton = this.addButtonTargets.find((b) => parseInt(b.dataset.index) === index);

    if (preview && addButton) {
      const reader = new FileReader();
      reader.onload = (e) => {
        preview.src = e.target.result;
        preview.classList.remove('hidden');
        addButton.classList.add('hidden');
      };
      reader.readAsDataURL(file);
    }
  }

  // Show/hide loading spinner
  showSpinner(index) {
    const spinner = this.spinnerTargets.find((s) => parseInt(s.dataset.index) === index);
    if (spinner) {
      spinner.classList.remove('hidden');
    }
  }

  hideSpinner(index) {
    const spinner = this.spinnerTargets.find((s) => parseInt(s.dataset.index) === index);
    if (spinner) {
      spinner.classList.add('hidden');
    }
  }

  // Show delete button
  showDeleteButton(index) {
    const deleteBtn = this.deleteButtonTargets.find((b) => parseInt(b.dataset.index) === index);
    if (deleteBtn) {
      deleteBtn.classList.remove('hidden');
    }
  }

  // Delete uploaded media
  deleteMedia(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index);

    // Remove from uploadedMedia array
    this.uploadedMedia = this.uploadedMedia.filter((m) => m.index !== index);

    // Reset UI for this slot
    const preview = this.previewTargets.find((p) => parseInt(p.dataset.index) === index);
    const addButton = this.addButtonTargets.find((b) => parseInt(b.dataset.index) === index);
    const deleteBtn = this.deleteButtonTargets.find((b) => parseInt(b.dataset.index) === index);

    if (preview) {
      preview.classList.add('hidden');
      preview.src = '';
    }
    if (addButton) {
      addButton.classList.remove('hidden');
    }
    if (deleteBtn) {
      deleteBtn.classList.add('hidden');
    }

    this.updateAddButtonVisibility();
    this.validateForm();
  }

  // Update add button visibility (only show on first empty slot)
  updateAddButtonVisibility() {
    const usedIndices = this.uploadedMedia.map((m) => m.index);

    this.addButtonTargets.forEach((button) => {
      const index = parseInt(button.dataset.index);
      const isUsed = usedIndices.includes(index);

      if (isUsed) {
        button.classList.add('hidden');
      } else {
        // Show only if it's the first empty slot
        const firstEmptyIndex = this.getNextAvailableSlotIndex();
        if (index === firstEmptyIndex) {
          button.classList.remove('hidden');
        } else {
          button.classList.add('hidden');
        }
      }
    });
  }

  // Handle star rating selection
  handleRating(event) {
    event.preventDefault();
    const target = event.target.closest('[data-rating]');
    if (!target) return;

    const rating = parseInt(target.dataset.rating);
    this.selectedRating = rating;
    this.ratingInputTarget.value = rating;

    // Update star colors
    this.starTargets.forEach((star, index) => {
      const starRating = parseInt(star.dataset.rating);
      const svg = star.querySelector('svg');

      if (starRating <= rating) {
        // Filled star
        svg.classList.remove('text-[#D1D5DB]');
        svg.classList.add('text-[#F59E0B]');
      } else {
        // Empty star
        svg.classList.remove('text-[#F59E0B]');
        svg.classList.add('text-[#D1D5DB]');
      }
    });

    this.validateForm();
  }

  getCsrfToken() {
    const tokenElement = this.element.querySelector('[name=csrfmiddlewaretoken]');
    if (tokenElement) {
      return tokenElement.value;
    }
    const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  // Validate form and enable/disable submit button
  validateForm() {
    const hasImages = this.uploadedMedia.length > 0;
    const hasDescription = this.descriptionTarget.value.trim().length > 0;
    const hasRating = this.selectedRating > 0;

    const isValid = hasImages && hasDescription && hasRating;

    // Update hidden input with S3 keys (comma-separated)
    if (this.hasS3KeysInputTarget) {
      const s3Keys = this.uploadedMedia.map((m) => m.s3_key).join(',');
      this.s3KeysInputTarget.value = s3Keys;
    }

    if (isValid) {
      this.submitButtonTarget.disabled = false;
      this.submitButtonTarget.classList.remove('cursor-not-allowed', 'opacity-50');
      this.submitButtonTarget.classList.add('hover:bg-[#0FEED7]');
    } else {
      this.submitButtonTarget.disabled = true;
      this.submitButtonTarget.classList.add('cursor-not-allowed', 'opacity-50');
      this.submitButtonTarget.classList.remove('hover:bg-[#0FEED7]');
    }
  }

}
